import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface STEADIFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  '/52552-7': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97875-9': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97876-7': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97877-5': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97878-3': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97879-1': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97880-9': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97881-7': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97882-5': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97883-3': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/97884-1': z.enum(['LA33-6', 'LA32-8']).nullable(),
  '/48527-6': z.enum(['LA33-6', 'LA32-8']).nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.steadiQuesionnaire)

export function STEADIForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: STEADIFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection
        title="I have fallen in the past year."
        subtitle="People who have fallen once are likely to fall again."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/52552-7"
          suggestion={fieldSuggestions?.['/52552-7']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (2)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I use or have been advised to use a cane or walker to get around safely."
        subtitle="People who have been advised to use a cane or walker may already be more likely to fall."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97875-9"
          suggestion={fieldSuggestions?.['/97875-9']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (2)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="Sometimes I feel unsteady when I am walking."
        subtitle="Unsteadiness or needing support while walking are signs of poor balance."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97876-7"
          suggestion={fieldSuggestions?.['/97876-7']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I steady myself by holding onto furniture when walking at home."
        subtitle="This is also a sign of poor balance."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97877-5"
          suggestion={fieldSuggestions?.['/97877-5']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I am worried about falling."
        subtitle="People who are worried about falling are more likely to fall."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97878-3"
          suggestion={fieldSuggestions?.['/97878-3']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I need to push with my hands to stand up from a chair."
        subtitle="This is a sign of weak leg muscles, a major reason for falling."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97879-1"
          suggestion={fieldSuggestions?.['/97879-1']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I have some trouble stepping up onto a curb."
        subtitle="This is also a sign of weak leg muscles."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97880-9"
          suggestion={fieldSuggestions?.['/97880-9']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I often have to rush to the toilet."
        subtitle="Rushing to the bathroom, especially at night, increases your chance of falling."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97881-7"
          suggestion={fieldSuggestions?.['/97881-7']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I have lost some feeling in my feet."
        subtitle="Numbness in your feet can cause stumbles and lead to falls."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97882-5"
          suggestion={fieldSuggestions?.['/97882-5']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I take medicine that sometimes makes me feel light-headed or more tired than usual."
        subtitle="Side effects from medicines can sometimes increase your chance of falling."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97883-3"
          suggestion={fieldSuggestions?.['/97883-3']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I take medicine to help me sleep or improve my mood."
        subtitle="These medicines can sometimes increase your chance of falling."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/97884-1"
          suggestion={fieldSuggestions?.['/97884-1']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection
        title="I often feel sad or depressed."
        subtitle="Symptoms of depression, such as not feeling well or feeling slowed down, are linked to falls."
      >
        <ValidatedToolRadioGroupField
          control={form.control}
          name="/48527-6"
          suggestion={fieldSuggestions?.['/48527-6']}
          options={[
            {
              value: 'LA33-6',
              label: 'Yes (1)',
            },
            {
              value: 'LA32-8',
              label: 'No (0)',
            },
          ]}
        />
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  return (
    <div className="flex items-center gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
    </div>
  )
}
